import { Component } from "@theme/component";
import { RecentlyViewed } from "@theme/recently-viewed-products";

class RecentlyViewedSection extends Component {
  /**
   * The observer for lazy loading
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: "0px 0px 400px 0px" },
  );

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Load the recently viewed products
   */
  #loadRecentlyViewed() {
    const { sectionId } = this.dataset;
    const minProducts = Number.parseInt(this.dataset.minProducts || "1", 10);
    const currentProductId = this.dataset.currentProductId?.trim();
    const id = this.id;
    const viewedProducts = RecentlyViewed.getProducts().filter(
      (/** @type {string} */ productId) => productId !== currentProductId,
    );

    if (!id || !sectionId) {
      this.#handleError(
        new Error("Section ID and an ID attribute are required"),
      );
      return;
    }

    if (viewedProducts.length < minProducts) {
      this.#handleError(new Error("No recently viewed products"));
      return;
    }

    const url = new URL(
      window.Theme?.routes?.search_url || "/search",
      location.origin,
    );
    url.searchParams.set(
      "q",
      viewedProducts
        .map(/** @param {string} productId */ (productId) => `id:${productId}`)
        .join(" OR "),
    );
    url.searchParams.set("type", "product");
    url.searchParams.set("section_id", sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    fetch(url.toString(), { signal: this.#activeFetch.signal })
      .then((response) => {
        if (!response.ok) {
          this.#handleError(new Error(`Server returned ${response.status}`));
          return;
        }
        return response.text();
      })
      .then((html) => {
        if (!html) return;

        const parsed = document.createElement("div");
        parsed.innerHTML = html;
        const section = parsed.querySelector(
          `recently-viewed-section[id="${id}"]`,
        );
        const hasRecommendations = section
          ?.querySelector("[data-has-recommendations]")
          ?.getAttribute("data-has-recommendations");

        if (hasRecommendations === "false") {
          this.#handleError(
            new Error("Not enough recently viewed products to render section"),
          );
          return;
        }

        if (section?.innerHTML && section.innerHTML.trim().length) {
          this.innerHTML = section.innerHTML;
        } else {
          this.#handleError(new Error("No recently viewed products available"));
        }
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        this.#handleError(e);
      })
      .finally(() => {
        this.#activeFetch = null;
      });
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error("Recently viewed products error:", error.message);
    this.classList.add("hidden");
    this.dataset.error = "Error loading recently viewed products";
  }
}

if (!customElements.get("recently-viewed-section")) {
  customElements.define("recently-viewed-section", RecentlyViewedSection);
}
